import React from 'react';
import {
  Dialog,
  DialogContent,
} from './ui/dialog';
import { Badge } from './ui/badge';
import { Bell, CheckCircle2, AlertTriangle, Info, X, Loader2 } from 'lucide-react';
import { useNotifications } from '../hooks/api/notifications';

// Severity → visual config (icons + colors). Mock data array deleted —
// the dropdown reads live from /api/v1/notifications.
const SEVERITY_VISUALS = {
  critical: {
    icon: AlertTriangle,
    iconColor: '#f87171',
    glowColor:  'rgba(239, 68, 68, 0.30)',
    bgColor:    'rgba(239, 68, 68, 0.12)',
  },
  warning: {
    icon: AlertTriangle,
    iconColor: '#fbbf24',
    glowColor:  'rgba(251, 191, 36, 0.25)',
    bgColor:    'rgba(251, 191, 36, 0.12)',
  },
  info: {
    icon: Info,
    iconColor: '#60a5fa',
    glowColor:  'rgba(59, 130, 246, 0.25)',
    bgColor:    'rgba(59, 130, 246, 0.12)',
  },
};

function relativeTime(iso) {
  if (!iso) return '';
  const t = new Date(iso).getTime();
  const diffMs = Date.now() - t;
  const m = Math.round(diffMs / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m} min${m === 1 ? '' : 's'} ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h} hour${h === 1 ? '' : 's'} ago`;
  const d = Math.round(h / 24);
  return `${d} day${d === 1 ? '' : 's'} ago`;
}

export function NotificationDialog({ open, onOpenChange }) {
  const { data, isLoading } = useNotifications({ page_size: 10 });
  const liveItems = (data?.data || []).map((n) => {
    const sev = SEVERITY_VISUALS[n.type] || SEVERITY_VISUALS.info;
    return {
      id: n.id,
      icon: sev.icon,
      iconColor: sev.iconColor,
      glowColor: sev.glowColor,
      bgColor:   sev.bgColor,
      title: n.title,
      message: n.body || '',
      time: relativeTime(n.created_at),
      unread: !n.read_at,
    };
  });
  const notifications = liveItems;
  const unreadCount = notifications.filter((n) => n.unread).length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[480px] p-0 overflow-hidden border-none bg-transparent shadow-none">
        <div style={styles.card}>
          {/* Header */}
          <div style={styles.header}>
            <div style={styles.headerTitleRow}>
              <div style={styles.iconCircle}>
                <Bell className="w-5 h-5 text-sky-400" />
                <span style={styles.notificationPulse}></span>
              </div>
              <div style={{ flex: 1 }}>
                <div style={styles.titleBadgeRow}>
                  <h2 style={styles.title}>Notifications</h2>
                  <div style={styles.badge}>
                    {unreadCount > 0 ? `${unreadCount} New` : 'All read'}
                  </div>
                </div>
                <p style={styles.subtitle}>Stay updated with your latest project activities</p>
              </div>
            </div>
            <button 
              onClick={() => onOpenChange(false)} 
              style={styles.closeBtn}
            >
              <X size={18} />
            </button>
          </div>

          {/* List Area */}
          <div style={styles.listArea}>
            <div style={styles.scrollContainer} className="hide-scrollbar">
              {isLoading && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: 24, color: 'rgba(148,163,184,0.7)', fontSize: 13 }}>
                  <Loader2 className="w-4 h-4 animate-spin" /> Loading notifications…
                </div>
              )}
              {!isLoading && notifications.length === 0 && (
                <div style={{ padding: 32, textAlign: 'center', color: 'rgba(148,163,184,0.7)', fontSize: 13 }}>
                  You're all caught up — no notifications.
                </div>
              )}
              {notifications.map((notification) => {
                const Icon = notification.icon;
                return (
                  <div
                    key={notification.id}
                    style={{
                      ...styles.notificationItem,
                      ...(notification.unread ? styles.unreadItem : {})
                    }}
                    className="notification-hover"
                  >
                    <div style={styles.itemContent}>
                      <div
                        style={{
                          ...styles.itemIconWrapper,
                          background: notification.bgColor,
                          boxShadow: `0 0 15px ${notification.glowColor}`,
                        }}
                      >
                        <Icon className="w-5 h-5" style={{ color: notification.iconColor }} />
                      </div>
                      <div style={styles.itemTextContent}>
                        <div style={styles.itemHeader}>
                          <h4 style={styles.itemTitle}>
                            {notification.title}
                          </h4>
                          <span style={styles.itemTime}>{notification.time}</span>
                        </div>
                        <p style={styles.itemMessage}>
                          {notification.message}
                        </p>
                        {notification.unread && (
                           <div style={styles.unreadStatus}>
                             <div style={styles.unreadDot}></div>
                             <span>New Activity</span>
                           </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Footer */}
          <div style={styles.footer}>
             <button style={styles.actionBtn}>Mark all as read</button>
             <button style={styles.viewAllBtn} onClick={() => onOpenChange(false)}>Close Dialog</button>
          </div>
        </div>

        <style>{`
          .notification-hover {
            transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
          }
          .notification-hover:hover {
            background: rgba(255, 255, 255, 0.03) !important;
            transform: translateX(4px);
          }
          /* Custom scrollbar hiding but functional */
          [data-slot="select-content"], .hide-scrollbar {
             scrollbar-width: none;
             -ms-overflow-style: none;
          }
          .hide-scrollbar::-webkit-scrollbar {
            display: none;
          }
          @keyframes pulse {
            0% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(56, 189, 248, 0.7); }
            70% { transform: scale(1); box-shadow: 0 0 0 6px rgba(56, 189, 248, 0); }
            100% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(56, 189, 248, 0); }
          }
        `}</style>
      </DialogContent>
    </Dialog>
  );
}

const styles = {
  card: {
    background: 'rgba(8, 12, 24, 0.98)',
    backdropFilter: 'blur(24px)',
    WebkitBackdropFilter: 'blur(24px)',
    border: '1px solid rgba(255, 255, 255, 0.08)',
    borderRadius: '24px',
    boxShadow: '0 30px 60px -12px rgba(0, 0, 0, 0.6), 0 0 0 1px rgba(255,255,255,0.05) inset',
    width: '100%',
    overflow: 'hidden',
    fontFamily: '"Inter", sans-serif',
  },
  header: {
    padding: '24px 28px',
    borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
  },
  headerTitleRow: {
    display: 'flex',
    gap: '16px',
    alignItems: 'flex-start',
    flex: 1,
  },
  iconCircle: {
    width: '44px',
    height: '44px',
    borderRadius: '12px',
    background: 'rgba(56, 189, 248, 0.1)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: '1px solid rgba(56, 189, 248, 0.15)',
    position: 'relative',
  },
  notificationPulse: {
    position: 'absolute',
    top: '10px',
    right: '10px',
    width: '8px',
    height: '8px',
    background: '#38bdf8',
    borderRadius: '50%',
    animation: 'pulse 2s infinite',
  },
  titleBadgeRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '2px',
  },
  title: {
    margin: 0,
    fontSize: '20px',
    fontWeight: '600',
    color: '#fff',
    letterSpacing: '-0.02em',
  },
  badge: {
    padding: '4px 10px',
    background: 'rgba(56, 189, 248, 0.15)',
    color: '#38bdf8',
    fontSize: '11px',
    fontWeight: '700',
    borderRadius: '20px',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  subtitle: {
    margin: 0,
    fontSize: '13px',
    color: 'rgba(148, 163, 184, 0.6)',
    lineHeight: '1.4',
  },
  closeBtn: {
    background: 'rgba(255, 255, 255, 0.03)',
    border: '1px solid rgba(255, 255, 255, 0.05)',
    color: 'rgba(148, 163, 184, 0.5)',
    cursor: 'pointer',
    padding: '6px',
    borderRadius: '10px',
    transition: 'all 0.2s',
  },
  listArea: {
    padding: '16px 8px',
  },
  scrollContainer: {
    maxHeight: '420px',
    overflowY: 'auto',
    padding: '0 16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  notificationItem: {
    padding: '16px',
    borderRadius: '16px',
    background: 'rgba(255, 255, 255, 0.01)',
    border: '1px solid rgba(255, 255, 255, 0.04)',
    cursor: 'pointer',
  },
  unreadItem: {
    background: 'rgba(56, 189, 248, 0.02)',
    borderColor: 'rgba(56, 189, 248, 0.1)',
  },
  itemContent: {
    display: 'flex',
    gap: '14px',
    alignItems: 'flex-start',
  },
  itemIconWrapper: {
    width: '40px',
    height: '40px',
    borderRadius: '10px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    border: '1px solid rgba(255, 255, 255, 0.05)',
  },
  itemTextContent: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  itemHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
  },
  itemTitle: {
    margin: 0,
    fontSize: '14.5px',
    fontWeight: '600',
    color: '#f8fafc',
  },
  itemTime: {
    fontSize: '11.5px',
    color: 'rgba(148, 163, 184, 0.5)',
  },
  itemMessage: {
    margin: 0,
    fontSize: '13px',
    color: 'rgba(148, 163, 184, 0.7)',
    lineHeight: '1.5',
  },
  unreadStatus: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    marginTop: '4px',
    fontSize: '11px',
    fontWeight: '600',
    color: '#38bdf8',
  },
  unreadDot: {
    width: '6px',
    height: '6px',
    borderRadius: '50%',
    background: '#38bdf8',
    boxShadow: '0 0 10px #38bdf8',
  },
  footer: {
    padding: '20px 28px 24px',
    borderTop: '1px solid rgba(255, 255, 255, 0.05)',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    background: 'rgba(255, 255, 255, 0.01)',
  },
  actionBtn: {
    background: 'transparent',
    border: 'none',
    fontSize: '13px',
    fontWeight: '600',
    color: 'rgba(56, 189, 248, 0.9)',
    cursor: 'pointer',
    padding: 0,
  },
  viewAllBtn: {
    padding: '8px 16px',
    background: 'rgba(255, 255, 255, 0.04)',
    border: '1px solid rgba(255, 255, 255, 0.08)',
    borderRadius: '10px',
    color: '#fff',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
  }
};
